// TanStack Router pathless layout guard (T04 §9.5): every route under routes/_authenticated/ needs a
// session. Anonymous users are sent to /login with the original URL. The idle timer (CR-4) and the
// user menu (Log out) live here so every authenticated page has them.
import * as React from "react";
import {
  createFileRoute,
  redirect,
  Link,
  Outlet,
  useNavigate,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { applyRouteChange } from "@/features/shell/route-change";
import { useBuildInfo } from "@/features/shell/use-build-info";
import { Button } from "@chartvn/ui/button";
import { TopBar } from "@chartvn/ui/components/top-bar/top-bar";
import { SideNav } from "@chartvn/ui/components/side-nav/side-nav";
import { PageHeader } from "@chartvn/ui/components/page-header/page-header";
import { StatusRegion } from "@chartvn/ui/components/status-region/status-region";
import { BuildMarker } from "@chartvn/ui/components/build-marker/build-marker";
import { ImpersonationBar } from "@chartvn/ui/components/impersonation-bar/impersonation-bar";
import { authClient } from "../features/auth/client";
import { switchTenantContext } from "../features/auth/api/tenant-context";
import {
  Activity,
  Beaker,
  Building2,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Contact,
  FileText,
  Globe,
  HeartPulse,
  LayoutGrid,
  ListChecks,
  MailPlus,
  Mails,
  PhoneForwarded,
  Server,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  Upload,
} from "lucide-react";
import { adminDestinationsFor, withheldSentence } from "../features/auth/admin-destinations";
import { useIdleLogout } from "../features/auth/use-idle-logout";
import { useAssignments, usePermission } from "../features/auth/use-permission";
import {
  landsOnPlatformConsole,
  reachesPlatformConsole,
} from "../features/platform/platform-access";
import { useImpersonation } from "../features/platform/use-impersonation";
import type { HeldAssignment } from "@chartvn/schema/permissions";
import { setTelemetryUser } from "../telemetry";
import { navCurrent } from "../app/nav-current";

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: async ({ location, cause }) => {
    const { data: session } = await authClient.getSession();
    if (!session) {
      throw redirect({ to: "/login", search: { redirect: location.href, reason: undefined } });
    }
    setTelemetryUser(session.user.id); // opaque id only (T7/T12)
    // THE PLATFORM OPERATOR'S LANDING PAGE IS ITS CONSOLE (`RPM-064`, `J-297`). Entering the
    // application at the default landing, `/playground` — after sign-in or from `/` — sends an account
    // that reaches the console and holds nothing in a tenant to `/platform`. Only on entering: a later
    // click on Playground from inside the application (`cause: "stay"`) opens it as for anyone.
    const { assignments = [] } = session as { assignments?: readonly HeldAssignment[] };
    if (
      cause === "enter" &&
      location.pathname === "/playground" &&
      landsOnPlatformConsole(assignments)
    )
      throw redirect({ to: "/platform" });
    return { session };
  },
  component: AuthenticatedLayout,
});

/**
 * Each administration destination's icon, keyed by its id in `ADMIN_DESTINATIONS`. The words, the
 * order and the gate are the schema's; the picture is the shell's. ONBOARDING (`RPM-061`, `J-295`)
 * sits after Readiness, now that its screen exists (`J-057`: a nav item is a promise of a screen).
 */
const ADMIN_ICONS: Record<string, React.ReactNode> = {
  facilities: <Clock />,
  readiness: <ListChecks />,
  onboarding: <ClipboardCheck />,
  "vital-definitions": <Activity />,
  "band-sets": <SlidersHorizontal />,
  "care-units": <Building2 />,
  routing: <PhoneForwarded />,
  contacts: <Contact />,
  "standing-copies": <MailPlus />,
  "message-content": <FileText />,
  "routing-import": <Upload />,
  "source-systems": <Server />,
  "practice-configurations": <Settings2 />,
  roles: <ShieldCheck />,
};

/**
 * The primary navigation. Every entry points at a route that exists — a nav item is a promise that a
 * screen is there, and an "Administration" item pointing at an unregistered `/admin` sent a click out
 * of the application to a 404 (J-057). THREE ENTRIES, THREE SCREENS, NO STAND-INS: the five that
 * pointed at `/playground` in place of a screen that is not built were removed on 2026-09-22
 * (`D-859`), because a menu that shows where the application is going is indistinguishable, to
 * somebody clicking it, from one that lies about where it is.
 */
const NAV_ITEMS = [
  // FIVE ENTRIES WERE REMOVED HERE, AND `J-057` IS THE RULE THEY BROKE: a nav item is a promise that
  // a screen is there. Worklist, Results, Messages, Patients and Schedule all pointed at
  // `/playground` as a stand-in, so the first thing an audience clicks landed on a component
  // gallery. They are REMOVED rather than disabled, which is the shape this repository already
  // follows — `side-nav.tsx:15`: "An item the user has no capability for is NOT RENDERED. It is not
  // disabled." The component has no `disabled` prop, so removal was also the only shape available.
  // Each returns with the item that builds its screen.
  //
  // WORKLIST IS THE FIRST TO COME BACK (`J-454`), and it comes back because `/worklist` is now a
  // registered route that renders `RPM-004`'s queue. `J-454`'s own next step said the two land
  // together or neither does; this is that. Results, Messages, Patients and Schedule are still out.
  //
  // ENTER VITALS IS THE PROJECT OWNER'S INSTRUCTION, NOT A REQUIREMENT (`D-822`, `J-214`). No `RPM`
  // block names the side navigation except `RPM-092` (chunk 8); `RPM-096` and `RPM-097` name no nav
  // surface at all. Without this line the screen `J-213` built is reachable only by typing its URL.
  // The file's own rule holds: "a nav item is a promise that a screen is there" (`J-057`), and
  // `/vitals-entry` is a registered route that renders today.
  {
    id: "vitals-entry",
    label: "Enter vitals",
    href: "/vitals-entry",
    section: "Work",
    icon: <HeartPulse />,
  },
  {
    id: "worklist",
    label: "Worklist",
    href: "/worklist",
    section: "Work",
    icon: <ClipboardList />,
  },
  // A BUILDING'S SUMMARIES (`RPM-103`, `J-489`): prepared by ChartVN, sent by a nurse from her own
  // mail and marked sent here. Listed for an account that reads them; Mark sent asks for more.
  {
    id: "summaries",
    label: "Summaries",
    href: "/summaries",
    section: "Work",
    icon: <Mails />,
    requires: { summary: ["read"] },
  },
  {
    id: "playground",
    label: "Playground",
    href: "/playground",
    section: "System",
    icon: <LayoutGrid />,
  },
  {
    id: "rpm-bench",
    label: "RPM bench",
    href: "/rpm-bench",
    section: "System",
    icon: <Beaker />,
  },
];

function AuthenticatedLayout() {
  const { session } = Route.useRouteContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { warning, stayActive } = useIdleLogout();
  const build = useBuildInfo();
  // The rail's collapsed state lives here, not in SideNav: the component is presentational and a
  // consumer may want to persist the choice. Without this the Collapse button rendered and did
  // nothing, because `onToggleCollapsed` was never passed (J-057).
  const [navCollapsed, setNavCollapsed] = React.useState(false);
  // The Work entries' gates, one hook call each: hooks cannot run in a loop. The Summaries list route
  // accepts a building assignment only (`summaries.routes.ts`), so the menu asks at that kind (`J-649`).
  const granted: Record<string, boolean> = {
    summaries: usePermission({ summary: ["read"] }, ["CERTIFIED_FACILITY"]),
  };
  // THE ADMINISTRATION SECTION (`RPM-092`, `D-1540`, `J-649`): `ADMIN_DESTINATIONS` in its order, each
  // shown when an assignment at a scope its read route accepts grants that read, and what is withheld
  // said in words under the section rather than left to be noticed (`RPM-057`).
  const admin = adminDestinationsFor(useAssignments());
  const adminItems = admin.shown.map(({ id, label, href }) => ({
    id,
    label,
    href,
    section: "Administration",
    icon: ADMIN_ICONS[id],
  }));
  const workItems = NAV_ITEMS.filter(
    (item) => !("requires" in item) || granted[item.id] === true,
  ).map(({ id, label, href, section, icon }) => ({ id, label, href, section, icon }));
  // THE PLATFORM CONSOLE (`RPM-064`, `J-297`): offered to an account holding a `PLATFORM_OPERATOR` at
  // `PLATFORM` scope, and to no tenant account (`platform-access.ts`).
  const platformItems = reachesPlatformConsole(useAssignments())
    ? [
        {
          id: "platform",
          label: "Platform console",
          href: "/platform",
          section: "Platform",
          icon: <Globe />,
        },
      ]
    : [];
  // Platform, then Work, then Administration, then System: the platform console first, for the one
  // account that has it; then the order the rail has always drawn its sections in.
  const navItems = [
    ...platformItems,
    ...workItems.filter((item) => item.section === "Work"),
    ...adminItems,
    ...workItems.filter((item) => item.section !== "Work"),
  ];
  const withheld = withheldSentence(admin);

  // The screen's own name and path, read from the deepest matched route. This layout stays mounted
  // while the outlet swaps children, so anything derived from "the current screen" has to come from
  // router state — a value written here would be pinned to whichever screen was first.
  const { pathname, screenName } = useRouterState({
    select: (state) => {
      // Leaf first: the deepest route that names a screen wins, and a route that declares nothing
      // inherits from its parent instead of falling back to a placeholder.
      const named = [...state.matches].reverse().find((m) => m.staticData?.screenName);
      return {
        pathname: state.location.pathname,
        screenName: named?.staticData?.screenName ?? "ChartVN",
      };
    },
  });

  // The obligations of a route change (22-COMPOUNDS-SHELL §2.1). None of them happens by itself on a
  // client-side navigation: the browser only does them on a document load. Keyed on the PATHNAME, not
  // on mount — this layout is pathless, so React keeps it mounted across every child-to-child
  // navigation and a `[]` dependency would run the obligations exactly once, ever.
  React.useEffect(() => {
    applyRouteChange({ screenName });
  }, [pathname, screenName]);

  // THE SESSION'S ONE TENANT (`RPM-067`, `J-269`), as the session itself carries it: the server's
  // `customSession` adds `tenantContext` and `tenantChoices`, so the top bar costs no request of its
  // own. A switch ends everything cached for the tenant being left: the query cache is cleared as it
  // is at sign-out, and the router re-runs `beforeLoad` so the session is read again.
  const router = useRouter();
  const { tenantContext = null, tenantChoices = [] } = session as unknown as {
    tenantContext?: string | null;
    tenantChoices?: { tenantId: string; legalName: string }[];
  };
  const [tenantAnnouncement, setTenantAnnouncement] = React.useState("");
  const switchTenant = useMutation({
    mutationFn: switchTenantContext,
    onSuccess: async (next) => {
      queryClient.clear();
      setTenantAnnouncement(`You are now working in ${next.active?.legalName ?? "no tenant"}.`);
      await router.invalidate();
    },
  });

  // IMPERSONATION (`RPM-064`'s Keyboard row, `J-297`): whom the session acts as, named in the top bar
  // and in the band under it while it lasts, announced assertively on entering and on leaving. Stop
  // drops everything cached for the administrator and returns to the console.
  const impersonation = useImpersonation(session, async () => {
    queryClient.clear();
    authClient.$store.notify("$sessionSignal");
    await router.invalidate();
    await navigate({ to: "/platform" });
  });
  const moduleName =
    impersonation.actingAs !== null
      ? `Impersonating ${impersonation.actingAs}`
      : pathname === "/platform" || pathname.startsWith("/platform/")
        ? "Platform operator"
        : "Charting";

  async function logout() {
    await authClient.signOut();
    queryClient.clear(); // no PHI stays in memory (T12)
    setTelemetryUser(undefined);
    await navigate({ to: "/login", search: { redirect: undefined, reason: undefined } });
  }

  /**
   * LOCKING ENDS THE SESSION AND KEEPS THE PLACE (`RPM-089` row 3): "a session lock mid-workup,
   * then re-authentication — the workup resumes intact at the same alert". Everything the workup
   * holds is on the server; the address names the building and the alert by opaque ids, so signing
   * back in returns to it. Nothing about the resident stays in the browser.
   */
  async function lock() {
    const returnTo = router.state.location.href;
    await authClient.signOut();
    queryClient.clear();
    setTelemetryUser(undefined);
    await navigate({ to: "/login", search: { redirect: returnTo, reason: "locked" } });
  }

  return (
    // A1, the workspace archetype: the shell surrounds the content rather than the content carrying
    // its own chrome. Every part here is a @chartvn/ui shell component, which is also what satisfies
    // CDoD-11 — a component is only "reachable" once a real ChartVN screen uses it (D-107).
    // h-screen, not min-h-screen: the rail stretches to the row, and the row can only have a
    // definite height if the column above it does. With min-height the height is indefinite and
    // the rail stops at its own content (J-059).
    //
    // PINNED TO THE WINDOW, `fixed inset-0` (J-572): the shell IS the window, so nothing inside it can
    // make the page taller than the window and scroll the top bar and rail away. With `h-screen` alone
    // an entry's hidden announcements and inputs, placed `absolute` with no positioned ancestor, grew
    // the page to 2,652 px in a 779 px window, and a wheel past the content's end scrolled it all up.
    <div className="fixed inset-0 flex flex-col overflow-hidden">
      <TopBar
        productName="ChartVN"
        // "Platform operator" on the console and "Impersonating <account>" while one lasts
        // (`RPM-064`): the product area names which surface this is, in words.
        moduleName={moduleName}
        // Literal, and rendered whenever this is not production. Hiding it in a production-like
        // training system is the failure §4.2 exists to prevent: wrong-environment charting.
        // VITE_APP_ENV is the variable this app already declares in vite-env.d.ts and reads in
        // telemetry.ts; a second name for the same fact would be one more thing to keep in step.
        environment={import.meta.env.VITE_APP_ENV ?? import.meta.env.MODE}
        // Role and location, not just the person: on a shared workstation this is the primary
        // defence against acting under a colleague's session (SI-11, SP-14).
        user={{
          name: session.user.email,
          role: session.user.role ?? "user",
          location: "unassigned",
        }}
        notificationCount={0}
        onNotifications={() => undefined}
        onHelp={() => undefined}
        onLockSession={() => void lock()}
        onSignOut={() => void logout()}
        tenant={{
          active:
            tenantChoices
              .filter((t) => t.tenantId === tenantContext)
              .map((t) => ({ id: t.tenantId, name: t.legalName }))[0] ?? null,
          choices: tenantChoices.map((t) => ({ id: t.tenantId, name: t.legalName })),
          onSwitch: (tenantId: string) => {
            switchTenant.mutate(tenantId);
          },
          announcement: tenantAnnouncement,
        }}
      />
      <ImpersonationBar
        actingAs={impersonation.actingAs}
        announcement={impersonation.announcement}
        onStop={impersonation.stop}
        stopping={impersonation.stopping}
        {...(impersonation.error === undefined ? {} : { error: impersonation.error })}
      />
      <div className="flex min-h-0 flex-1">
        <SideNav
          items={navItems}
          // Read from the path, not written as a literal: the item whose `href` is the path, or the
          // one it continues under, as a summary's own screen does (`RPM-103`). A lookup rather than an
          // index because the list's order is not part of it.
          currentId={navCurrent(navItems, pathname) ?? "playground"}
          collapsed={navCollapsed}
          onToggleCollapsed={() => setNavCollapsed((c) => !c)}
          {...(withheld === "" ? {} : { sectionNotes: { Administration: withheld } })}
          // Router links, not plain anchors: an anchor performs a full document load, which discards
          // the session's in-memory state and re-runs every query on a same-app navigation.
          renderLink={({ href, children, ...rest }) => (
            <Link to={href} {...rest}>
              {children}
            </Link>
          )}
        />
        <div className="flex min-h-0 flex-1 flex-col">
          {/* One h1 for the document, named by the screen the outlet is showing. The screens do not
              render their own: two h1s would make the focus move in obligation 1 land on the wrong one. */}
          <PageHeader heading={screenName} />
          {/* `relative`: what the content places `absolute` is placed in, and scrolls with, the content. */}
          <main className="relative flex-1 overflow-y-auto px-6 pb-6">
            <Outlet />
          </main>
        </div>
      </div>

      {/* Full width beneath the rail, as every archetype draws it (03- §2.3): the status region and
          the build marker belong to the window, not to the content column. Nesting them beside the
          rail left the rail ending short of the bottom, which is what the user saw. */}
      <StatusRegion />
      <BuildMarker
        version={build.version}
        release={build.release}
        environment={build.environment}
        pending={build.pending}
      />
      {warning && (
        <div
          role="alertdialog"
          aria-label="Still there?"
          className="bg-destructive/10 fixed inset-x-0 top-0 z-50 flex items-center justify-center gap-4 p-3 text-sm"
        >
          You will be signed out in one minute without activity.
          <Button size="sm" onClick={stayActive} data-telemetry="idle-stay">
            Stay signed in
          </Button>
        </div>
      )}
    </div>
  );
}
